import { PropTypes } from 'typed/imports';
import { ComponentEventHandler1, PureComponent, ReactEventHandler2 } from 'typed/components/utils';
import { ComponentElement, ReactElement, ReactNode, isValidElement } from 'react';
import { default as FormattedResource, Resource, isResource, resourcePropType } from '../FormattedResource/FormattedResource';
import { Appearance } from '../Listbox/Listbox';
import TextInput from 'components/common/TextInput/TextInput';
import Checkbox from '../Checkbox/Checkbox';
import { assertNever, toArray } from 'typed/utils';
import './MultiListbox.less';

export { Appearance };

export const valuePropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]);

export interface LabelProps {
    className?: string;
    children?: ReactNode;
}

export class Label extends PureComponent<LabelProps> {
    static readonly displayName = 'MultiListbox.Label';

    static readonly propTypes = { className: PropTypes.string };

    static readonly defaultProps = { className: '' };

    render(): ReactElement {
        throw new Error('MultiListbox.Label can be used only inside MultiListbox');
    }
}

export interface GroupProps<TValue> {
    id: TValue;
    title: ReactElement | Resource;
    disabled?: boolean;
    className?: string;
    children?: Children<TValue>;
}

export class Group<TValue> extends PureComponent<GroupProps<TValue>> {
    static readonly displayName = 'MultiListbox.Group';

    static readonly propTypes = {
        id: valuePropType.isRequired,
        title: PropTypes.oneOfType([resourcePropType, PropTypes.element]),
        disabled: PropTypes.bool,
        className: PropTypes.string
    };

    static readonly defaultProps = {
        className: ''
    };

    render(): ReactElement {
        throw new Error('MultiListbox.Group can be used only inside MultiListbox');
    }
}

export interface OptionProps<TValue> {
    value: TValue;
    keywords?: string;
    disabled?: boolean;
    className?: string;
    children?: ReactNode;
}

export class Option<TValue> extends PureComponent<OptionProps<TValue>> {
    static readonly displayName = 'MultiListbox.Option';

    static readonly propTypes = {
        value: valuePropType,
        keywords: PropTypes.string,
        disabled: PropTypes.bool,
        className: PropTypes.string,
        style: PropTypes.object // TODO remove - styles should come from CSS
    };

    static readonly defaultProps = {
        className: '',
        style: {}
    };

    render(): ReactElement {
        throw new Error('MultiListbox.Option can be used only inside MultiListbox');
    }
}

export type Child<TValue> =
    | undefined | null
    | ComponentElement<LabelProps, Label>
    | ComponentElement<GroupProps<TValue>, Group<TValue>>
    | ComponentElement<OptionProps<TValue>, Option<TValue>>
    | ReactElement;

export type Children<TValue> = Child<TValue> | Child<TValue>[] | (Child<TValue> | Child<TValue>[])[];

const
    isImpossibleChild = (_child: ReactElement): _child is ReactElement => false,
    isLabel = (child: ReactElement): child is ReactElement<LabelProps> => !!child && typeof child === 'object' && child.type === Label,
    isGroup = <TValue extends any>(child: ReactElement): child is ReactElement<GroupProps<TValue>> => !!child && typeof child === 'object' && child.type === Group,
    isOption = <TValue extends any>(child: ReactElement): child is ReactElement<OptionProps<TValue>> => !!child && typeof child === 'object' && child.type === Option;

function keyify<TValue>(child: Child<TValue>, groupKey: string): { key: string; child: Child<TValue>; };
function keyify<TValue>(child: Child<TValue>[], groupKey: string): { key: string; child: Child<TValue>; }[];
function keyify<TValue>(child: Child<TValue> | Child<TValue>[], groupKey: string): { key: string; child: Child<TValue>; } | { key: string; child: Child<TValue>; }[] {
    return Array.isArray(child) ?
        child.map((subchild, subindex) => keyify(subchild, `${groupKey}.${subindex}`)) :
        {
            key: child?.key ? `external-${child.key}` : `index-${groupKey}`,
            child
        };
}

export interface Props<TValue> {
    value?: TValue[];
    appearance?: Appearance;
    disabled?: boolean;
    searchable?: boolean;
    searchPlaceholder?: Resource;
    clearSearchFilterAutomatically?: boolean;
    tools?: ReactElement[];
    groupSelectionEnabled?: boolean; // TODO unify groups and options somehow, at least id and value props
    className?: string;
    children?: Children<TValue>;
    onChange?: ComponentEventHandler1<TValue[]>;
}

interface State<TValue> {
    groupExpansionStates: Map<TValue, true | false | undefined>;
    filter: string;
}

export default class MultiListbox<TValue extends number | string | boolean> extends PureComponent<Props<TValue>, State<TValue>> {
    static readonly Appearance = Appearance;
    static readonly valuePropType = valuePropType;
    static readonly Label = Label;
    static readonly Group = Group;
    static readonly Option = Option;

    static readonly propTypes = {
        value: PropTypes.arrayOf(valuePropType),
        appearance: PropTypes.oneOf(Object.values(Appearance)),
        disabled: PropTypes.bool,
        searchable: PropTypes.bool,
        searchPlaceholder: resourcePropType,
        clearSearchFilterAutomatically: PropTypes.bool,
        tools: PropTypes.arrayOf(PropTypes.element),
        groupSelectionEnabled: PropTypes.bool, // TODO unify groups and options somehow, at least id and value props
        className: PropTypes.string,
        onChange: PropTypes.func,
        onSearchChange: PropTypes.func, // TODO remove - search is internal feature
        autofocusOnSearch: PropTypes.bool // TODO remove - there might be multiple MultiListboxes on one page
    };

    static readonly defaultProps = {
        value: [],
        appearance: Appearance.LINES,
        disabled: false,
        searchable: false,
        searchPlaceholder: { id: 'common.search' },
        clearSearchFilterAutomatically: true,
        groupSelectionEnabled: false,
        className: '',
        autofocusOnSearch: false
    };

    static readonly contextTypes = { intl: PropTypes.object };

    state: State<TValue> = {
        groupExpansionStates: new Map<TValue, true | false | undefined>(),
        filter: ''
    };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props<TValue>) {
        if (this.props.value !== nextProps.value) {
            this.setState({ groupExpansionStates: new Map([...this.state.groupExpansionStates].filter(([_, state]) => state)) });
        }

        if (DEBUG) {
            this._validateChildren(nextProps);
        }
    }

    private _validateChildren(props: Props<TValue> = this.props, children: Children<TValue> = props.children, usedValues: Set<TValue> = new Set<TValue>(), usedGroups: Set<TValue> = new Set<TValue>(), selectByParentID: TValue | null = null) {
        toArray(children || [])
            .flatten()
            .forEach((child) => {
                if (!child || isImpossibleChild(child) || isLabel(child)) { return; }

                if (isGroup<TValue>(child)) {
                    if (!props.groupSelectionEnabled) {
                        if (usedGroups.has(child.props.id)) {
                            throw new Error(`Duplicate ID in MultiListbox.Group: ${child.props.id}`);
                        }

                        usedGroups.add(child.props.id);
                    }
                    else {
                        if (usedValues.has(child.props.id)) {
                            throw new Error(`Duplicate value in MultiListbox.Group as Option: ${child.props.id}`);
                        }

                        if (selectByParentID && props.value!.includes(child.props.id)) {
                            throw new Error(`Duplicate selection. Value ${child.props.id} included into already selected Group: ${selectByParentID}.`);
                        }

                        usedValues.add(child.props.id);
                    }

                    this._validateChildren(props, child.props.children || null, usedValues, usedGroups, selectByParentID || (props.groupSelectionEnabled && props.value!.includes(child.props.id) ? child.props.id : null));

                    return;
                }

                if (isOption<TValue>(child)) {
                    if (usedValues.has(child.props.value)) {
                        throw new Error(`Duplicate value in MultiListbox.Option: ${child.props.value}`);
                    }

                    if (selectByParentID && props.value!.includes(child.props.value)) {
                        throw new Error(`Duplicate selection. Value ${child.props.value} included into already selected Group: ${selectByParentID}.`);
                    }

                    usedValues.add(child.props.value);

                    return;
                }

                assertNever(child, 'Children of MultiListbox must be instances of MultiListbox.Label, MultiListbox.Group or MultiListbox.Option.');
            });
    }

    private _expandValue(value: TValue[], children: Children<TValue> = this.props.children, selectByParent = false): TValue[] {
        if (!this.props.groupSelectionEnabled) {
            return value;
        }

        return toArray(children || [])
            .flatten()
            .filter(v => v)
            .map((child) => {
                if (!child || isImpossibleChild(child) || isLabel(child)) { return []; }

                if (isGroup<TValue>(child)) {
                    return (value.includes(child.props.id) ? [child.props.id] : []).concat(this._expandValue(value, child.props.children || null, selectByParent || value.includes(child.props.id)));
                }

                if (isOption<TValue>(child)) {
                    return value.includes(child.props.value) || selectByParent ? [child.props.value] : [];
                }

                return [];

            })
            .flatten();
    }

    private _collapseValue(value: TValue[], children: Children<TValue> = this.props.children, parentValue: TValue | null = null): TValue[] {
        if (!this.props.groupSelectionEnabled) {
            return value;
        }

        let allChildrenSelected = toArray(children || []).length > 0;

        const collapsed = toArray(children || [])
            .flatten()
            .filter(v => v)
            .map((child) => {
                if (!child || isImpossibleChild(child) || isLabel(child)) { return []; }

                if (isGroup<TValue>(child)) {
                    if (!toArray(child.props.children || []).length) {
                        const currentSelected = value.includes(child.props.id);
                        allChildrenSelected = allChildrenSelected && currentSelected;
                        return currentSelected ? [child.props.id] : [];
                    }
                    else {
                        const result = this._collapseValue(value, child.props.children || null, child.props.id);
                        allChildrenSelected = allChildrenSelected && result.length === 1 && result[0] === child.props.id;
                        return result;
                    }
                }

                if (isOption<TValue>(child)) {
                    const currentSelected = value.includes(child.props.value);
                    allChildrenSelected = allChildrenSelected && currentSelected;
                    return currentSelected ? [child.props.value] : [];
                }

                return [];
            })
            .flatten();

        if (allChildrenSelected && parentValue) {
            return [parentValue];
        }
        else {
            return collapsed;
        }
    }

    private _onFilterChange: ComponentEventHandler1<string> = (_sender, value) => {
        if ((this.props as any).onSearchChange) {
            (this.props as any).onSearchChange(_sender, value);
        }

        this.setState({ filter: value });
    };

    private _onOptionClick: ReactEventHandler2<TValue[], TValue[]> = (currentExplicitValue, change) => {
        this._onOptionChange(currentExplicitValue, change, this, null);
    };

    private _onOptionChange = (currentExplicitValue: TValue[], change: TValue[], _sender: unknown, selected: boolean | null) => {
        if (this.props.onChange && !this.props.disabled) {
            if (selected === null) {
                selected = !change.every(value => currentExplicitValue.includes(value));
            }
            if (selected) {
                this.props.onChange(this, this._collapseValue(currentExplicitValue.concat(change).unique()));
            }
            else {
                this.props.onChange(this, this._collapseValue(currentExplicitValue.filter(v => !change.includes(v))));
            }

            if (this.props.clearSearchFilterAutomatically) {
                this.setState({ filter: '' });
            }
        }
    };

    private _onToggleGroupClick: ReactEventHandler2<TValue, boolean> = (groupID, expanded) => {
        this.setState({ groupExpansionStates: new Map(this.state.groupExpansionStates).set(groupID, expanded) });
    };

    private _getInnerText(component: ReactNode): string {
        if (!component || !isValidElement(component!)) {
            return typeof component === 'string' ? component : '';
        }

        const { resource } = component.props as any;
        if (isResource(resource)) {
            return typeof resource === 'string' ? resource : this.context.intl.formatMessage(resource);
        }

        if (isGroup<TValue>(component)) {
            return isResource(component.props.title) ?
                typeof component.props.title === 'string' ? component.props.title : this.context.intl.formatMessage(component.props.title) :
                this._getInnerText(component.props.title);
        }

        if (isOption<TValue>(component) && !!component.props.keywords) {
            return component.props.keywords;
        }

        return toArray(component.props && (component.props as any).children || []).map(child => this._getInnerText(child)).join(' ');
    }

    private _isVisible(child: Child<TValue>): boolean {
        let childInnerText: string;
        return !this.props.searchable || !this.state.filter ||

                !!(childInnerText = this._getInnerText(child).toLowerCase()) &&
                this.state.filter.toLowerCase().split(' ').every(word => childInnerText.includes(word));
    }

    private _renderChildren(children: Children<TValue> = this.props.children, explicitValue: TValue[] = this._expandValue(this.props.value!), statsToFill: { nestedValues: TValue[]; subselectedCount: number; } = { nestedValues: [], subselectedCount: 0 }, parent: Child<TValue> = null): ReactNode[] {
        return toArray(children || [])
            .map((child, index) => keyify(child as any as Child<TValue>[], `${index}`))
            .flatten()
            .map(({ key, child }) => {
                if (!child || isImpossibleChild(child)) { return null; }

                if (isLabel(child)) {
                    return <div key={key} className={`label ${child.props.className}`}>{child.props.children}</div>;
                }

                if (isGroup<TValue>(child)) {
                    const
                        { appearance, searchable, groupSelectionEnabled: selectable } = this.props,
                        { filter, groupExpansionStates } = this.state,
                        { id, title, disabled, className, children } = child.props,
                        stats: typeof statsToFill = { nestedValues: [], subselectedCount: 0 },
                        subchildren = this._renderChildren(children || null, explicitValue, stats, child),
                        expanded = !disabled && (
                            groupExpansionStates.get(id) === true ||
                            searchable && filter ||
                            stats.subselectedCount && groupExpansionStates.get(id) !== false ||
                            !subchildren.length
                        );

                    if (!stats.nestedValues.length) {
                        stats.nestedValues = [id];
                        stats.subselectedCount = +explicitValue.includes(id);
                    }

                    statsToFill.nestedValues = statsToFill.nestedValues.concat(stats.nestedValues);
                    statsToFill.subselectedCount += stats.subselectedCount;

                    return !searchable || !filter || selectable && this._isVisible(child) || subchildren.length ?
                        appearance === Appearance.LINES ?
                            <div key={`index-${key}`}
                                className={
                                    `group ${className
                                    }${!stats.subselectedCount ? '' : stats.subselectedCount !== stats.nestedValues.length ? ' semiselected' : ' selected'
                                    }${disabled ? ' disabled' : ''}`
                                }>
                                <span
                                    className={`toggle fa ${expanded ? 'fa-minus-square-o' : 'fa-plus-square-o'} `}
                                    onClick={!disabled ? this._onToggleGroupClick.bind(this, id, !expanded) : undefined} />
                                <span
                                    className="title"
                                    onClick={!disabled ? this._onOptionClick.bind(this, explicitValue, stats.nestedValues) : undefined} >
                                    {isResource(title) ? <FormattedResource resource={title} /> : title}
                                </span>
                                {expanded ? <div className="content">{subchildren}</div> : null}
                            </div> :
                            <div key={`index-${key}`} className={`group ${className}`}>
                                <div className="connector" ><div className="horizontal" /><div className="dot" /><div className="vertical" /></div>
                                <div className="title">{isResource(title) ? <FormattedResource resource={title} /> : title}</div>
                                <div className="content">{subchildren}</div>
                            </div> :
                        null;
                }

                if (isOption<TValue>(child)) {
                    const
                        { value, disabled, className, children } = child.props,
                        selected = explicitValue.includes(value);

                    statsToFill.nestedValues.push(child.props.value);
                    statsToFill.subselectedCount += +selected;

                    // element is visible if it's parent maches the filter or if it maches the filter
                    return parent && this._isVisible(parent) || this._isVisible(child) ?
                        this.props.appearance === Appearance.LINES ?
                            <div key={key}
                                className={
                                    `option ${className
                                    }${selected ? ' selected' : ''
                                    }${disabled ? ' disabled' : ''}`
                                }
                                style={(child.props as any).style}
                                onClick={!disabled ? this._onOptionClick.bind(this, explicitValue, [value]) : undefined}>
                                {children}
                            </div> :
                            <div key={key} className="option">
                                <div className="connector" ><div className="horizontal" /><div className="dot" /><div className="vertical" /></div>
                                <Checkbox appearance={Checkbox.Appearance.SWITCH} disabled={disabled} value={selected} onChange={!disabled ? this._onOptionChange.bind(this, explicitValue, [value]) : undefined}>
                                    {children}
                                </Checkbox>
                            </div> :
                        null;
                }

                return assertNever(child);
            })
            .filter(v => v);
    }

    render() {
        return (
            <div className={`multi-listbox ${this.props.appearance === Appearance.LINES ? 'lines ' : 'switches '}${this.props.className}`} >
                <div className="toolbar flex-row">
                    {this.props.searchable ? <div className="tool main-col"><TextInput value={this.state.filter} autofocus={(this.props as any).autofocusOnSearch} icon="search" placeholder={this.props.searchPlaceholder} onChange={this._onFilterChange} /></div> : null}
                    {this.props.tools ? toArray(this.props.tools).map((tool, index) => <div key={tool.key || index} className="tool">{tool}</div>) : null}
                </div>
                {/* TODO make this node focusable and controllable with up and down keys */}
                <div tabIndex={0} className="children" >
                    {this._renderChildren().or(this.state.filter ? <FormattedResource resource={{ id: 'search.nothing_found' }} /> : null)}
                </div>
            </div>
        );
    }
}
